import { useState, ChangeEvent, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  Input,
  Button,
  Stack,
  Divider,
  FormControl,
  FormLabel,
  Alert,
  IconButton,
} from '@mui/joy';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { 
  useLazyGetGeneralConfigurationQuery, 
  useUpdateGeneralConfigurationMutation,
  GlobalSettingsConfig,
  defaultGeneralConfiguration
} from '@consolidocs-ui/data-access/GlobalSettingsApi';
import { Loader } from '@consolidocs-ui/components';
import { useToast } from '@consolidocs-ui/utils';

export default function GlobalSettings() {
  const [settings, setSettings] = useState<GlobalSettingsConfig>(defaultGeneralConfiguration);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [configError, setConfigError] = useState<any>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [triggerToast] = useToast();
  
  // API hooks
  const [getGeneralConfiguration] = useLazyGetGeneralConfigurationQuery();
  const [updateConfiguration, { isLoading: isUpdating }] = useUpdateGeneralConfigurationMutation();

  // Fetch configuration on mount
  useEffect(() => {
    const fetchConfig = async () => {
      setIsLoadingConfig(true);
      try {
        const result = await getGeneralConfiguration('default-settings').unwrap();
        let configuration = null;
        
        // Handle different response structures
        if (result.data?.configuration) {
          configuration = result.data.configuration;
        } else if (result.configuration) {
          configuration = result.configuration;
        }
        
        if (configuration) {
          const mergedSettings = {
            ...defaultGeneralConfiguration,
            ...configuration,
          };
          setSettings(mergedSettings);
        }
        setConfigError(null);
      } catch (error) {
        console.error('Failed to fetch general configuration:', error);
        setConfigError(error);
        triggerToast({
          message: 'Failed to load saved settings. Using default configuration.',
          type: 'warning',
          direction: 'top-right',
        });
        // Settings already initialized with defaultGeneralConfiguration
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSettingChange = (key: keyof GlobalSettingsConfig, value: boolean | number) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      await updateConfiguration({
        name: 'default-settings',
        configuration: settings,
      }).unwrap();
      
      setSaveStatus('saved');
      triggerToast({
        message: 'Global settings saved successfully!',
        type: 'success',
        direction: 'top-right',
      });
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      triggerToast({
        message: 'Failed to save settings. Please try again.',
        type: 'danger',
        direction: 'top-right',
      });
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const getJsonPreview = () => {
    return JSON.stringify(settings, null, 2);
  };

  // Show loading state while fetching configuration
  if (isLoadingConfig) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography level="h2">
          Global Settings
        </Typography>
        <IconButton
          variant="outlined"
          color="neutral"
          onClick={() => setShowJsonPreview(!showJsonPreview)}
          sx={{ ml: 2 }}
        >
          {showJsonPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Box>
      
      <Typography level="body-md" sx={{ mb: 4, color: 'neutral.500' }}>
        Configure global application settings that will affect the behavior across all organizations.
      </Typography>

      {/* API Error Alert */}
      {configError && (
        <Alert color="warning" sx={{ mb: 3 }}>
          Failed to load saved settings. Using default configuration.
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 1, fontSize: 'sm' }}>
              Error: {JSON.stringify(configError, null, 2)}
            </Box>
          )}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        {/* Settings Form */}
        <Card sx={{ flex: showJsonPreview ? 1 : 2 }}>
          <CardContent>
            <Typography level="h4" sx={{ mb: 3 }}>
              Configuration Options
            </Typography>

            <Stack spacing={3}>
              {/* Add More Button */}
              <FormControl>
                <FormLabel>Add More Button</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch
                    checked={settings.addMoreButton}
                    onChange={(event) => handleSettingChange('addMoreButton', event.target.checked)}
                  />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Show "Add More" button in file upload interfaces
                  </Typography>
                </Box>
              </FormControl>

              <Divider />

              {/* File Upload Limit */}
              <FormControl>
                <FormLabel>File Upload Limit (per organization)</FormLabel>
                <Input
                  type="number"
                  value={settings.fileUploadLimit}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => handleSettingChange('fileUploadLimit', parseInt(event.target.value) || 0)}
                  slotProps={{
                    input: {
                      min: 1,
                      max: 1000,
                    },
                  }}
                  endDecorator="files"
                  sx={{ maxWidth: 200 }}
                />
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Maximum number of files that can be uploaded per organization
                </Typography>
              </FormControl>

              <Divider />

              {/* Show Sort Button */}
              <FormControl>
                <FormLabel>Show Sort Button</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch
                    checked={settings.showSortButton}
                    onChange={(event) => handleSettingChange('showSortButton', event.target.checked)}
                  />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Display sorting options in file lists
                  </Typography>
                </Box>
              </FormControl>

              <Divider />

              {/* Show Consolidate Button */}
              <FormControl>
                <FormLabel>Show Consolidate Button</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch
                    checked={settings.showConsolidateButton}
                    onChange={(event) => handleSettingChange('showConsolidateButton', event.target.checked)}
                  />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Show consolidation functionality to users
                  </Typography>
                </Box>
              </FormControl>

              <Divider />

              {/* Show Chatbot */}
              <FormControl>
                <FormLabel>Show Chatbot</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch
                    checked={settings.showChatbot}
                    onChange={(event) => handleSettingChange('showChatbot', event.target.checked)}
                  />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Enable chatbot assistance across the application
                  </Typography>
                </Box>
              </FormControl>

              <Divider />

              {/* Show Original File in Explorer */}
              <FormControl>
                <FormLabel>Show Original File in Explorer</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch
                    checked={settings.showOriginalFileInExplorer}
                    onChange={(event) => handleSettingChange('showOriginalFileInExplorer', event.target.checked)}
                  />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Allow users to view original files in the file explorer
                  </Typography>
                </Box>
              </FormControl>

              <Divider />
              {/* Show Original File in Explorer */}
              <FormControl>
                <FormLabel>Show Select All in Explorer</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch
                    checked={settings.showSelectAllInExplorer}
                    onChange={(event) => handleSettingChange('showSelectAllInExplorer', event.target.checked)}
                  />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Allow users to select all files in the file explorer
                  </Typography>
                </Box>
              </FormControl>
            </Stack>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                startDecorator={<SaveIcon />}
                onClick={handleSave}
                loading={saveStatus === 'saving' || isUpdating}
                disabled={saveStatus === 'saving' || isUpdating}
              >
                Save Settings
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* JSON Preview - conditionally rendered */}
        {showJsonPreview && (
          <Card sx={{ flex: 1, maxWidth: { lg: 400 } }}>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                JSON Configuration Preview
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: 'background.level1',
                  p: 2,
                  borderRadius: 'sm',
                  fontSize: 'sm',
                  fontFamily: 'var(--joy-fontFamily-body)',
                  fontVariantNumeric: 'tabular-nums',
                  overflow: 'auto',
                  maxHeight: 400,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {getJsonPreview()}
              </Box>
              <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
                This JSON represents the current configuration that will be saved to the system.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
}